import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, Filter, Search, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { parcelsApi } from '../../lib/api';
import { formatDate, formatPrice, getStatusLabel, getStatusColor } from '../../lib/utils';
import { toast } from 'sonner';

const ParcelsListPage = () => {
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchParcels();
  }, []);

  const fetchParcels = async () => {
    try {
      const response = await parcelsApi.getAll();
      setParcels(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const filteredParcels = parcels.filter(parcel => {
    const matchesFilter = filter === 'all' || parcel.status === filter;
    const matchesSearch = search === '' || 
      parcel.pickup_address.city.toLowerCase().includes(search.toLowerCase()) ||
      parcel.delivery_address.city.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="parcels-list-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-900 uppercase tracking-tight">
            Mes envois
          </h1>
          <p className="text-slate-600 mt-1">Gérez et suivez tous vos colis</p>
        </div>
        <Link to="/dashboard/new-parcel">
          <Button className="bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wide" data-testid="new-parcel-btn">
            Nouvel envoi
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Rechercher par ville..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-full md:w-[200px]" data-testid="filter-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="created">Créée</SelectItem>
                <SelectItem value="accepted">Acceptée</SelectItem>
                <SelectItem value="in_progress">En cours</SelectItem>
                <SelectItem value="delivered">Livrée</SelectItem>
                <SelectItem value="paid">Payée</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Parcels List */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="spinner mx-auto"></div>
              <p className="text-slate-500 mt-4">Chargement...</p>
            </div>
          ) : filteredParcels.length === 0 ? (
            <div className="p-8 text-center">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucun envoi trouvé</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredParcels.map((parcel) => (
                <Link 
                  key={parcel.id} 
                  to={`/dashboard/parcel/${parcel.id}`}
                  className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`parcel-${parcel.id}`}
                >
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                    <Package className="w-6 h-6 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900">
                      {parcel.pickup_address.city} → {parcel.delivery_address.city}
                    </p>
                    <p className="text-sm text-slate-500">
                      {parcel.weight}kg • {parcel.distance_km}km • {formatDate(parcel.created_at)}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(parcel.status)}`}>
                      {getStatusLabel(parcel.status)}
                    </span>
                    <p className="text-lg font-bold text-slate-900 mt-1">
                      {formatPrice(parcel.total_price)}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400" />
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ParcelsListPage;
